/* Bluebells Cakery — Personalised Postcard cart-drawer upsell
   Replaces the per-product "Add a personalised postcard" CVP step with one card
   in the cart drawer. Produces the SAME order shape the CVP step produced, so
   Order Printer Pro and the Make print pipeline need no changes:

     - a child line item titled "Personalised Postcard" carrying
       _Child / _ParentProductID / _group-data:type:child;group:<parent group>
       and "_Personalised Message" with the customer's text

   The message goes on the POSTCARD line, not the parent cake line. A cart
   transform merges each CVP bundle into one displayed line, so writing
   properties to the parent rewrites every line in the group and destroys the
   child markers. Adding the postcard in a single /cart/add.js call leaves the
   group intact. The message is mirrored into the cart attribute
   "Postcard Message" so the card can tell whether a postcard is already in the
   cart (merged child lines are invisible to /cart.js).

   Config comes from window.__bbPostcardUpsell (set in atlas-cvp-enhancements.js). */
(function () {
  var CFG = window.__bbPostcardUpsell;
  if (!CFG || !CFG.enabled || !CFG.variantId) return;

  var VARIANT = String(CFG.variantId);
  var TITLE = CFG.title || 'Personalised Postcard';
  var PRICE = CFG.priceLabel || '+$5.00';
  var IMAGE = CFG.imageUrl || '';
  var MAXLEN = CFG.maxChars || 300;
  var MSG_KEY = '_Personalised Message';
  var ATTR_KEY = 'Postcard Message';
  var ATTR_FOR = 'Postcard For';
  var META_KEY = 'Personalised Postcard';

  var busy = false, draft = '', chosenKey = '', wantFocus = false;
  var PRICE_CENTS = Math.round((parseFloat(String(PRICE).replace(/[^0-9.]/g, '')) || 0) * 100);
  // In-memory copy of the marker. The theme's cart.js re-posts the cart attributes
  // after every unguarded mutation using a snapshot taken BEFORE that mutation, so
  // a third-party cart change (a quantity tweak, say) can silently drop our keys.
  // We re-assert from here whenever we notice they have gone.
  var lastKnown = { msg: '', group: '' };

  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* One cart read shared by every Bluebells script. Both this file and the
     priority card poll the cart, and each Zapiet DOM pass used to trigger a
     fresh /cart.js - 24 of them on one load, queued behind each other, which
     is what made the widget feel slow. Requests inside the window share one
     promise; any cart mutation clears it. */
  function getCart() {
    var now = Date.now();
    var c = window.__bbCartCache;
    if (c && c.p && (now - c.t) < 600) return c.p;
    var p = fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .catch(function (e) { window.__bbCartCache = null; throw e; });
    window.__bbCartCache = { t: now, p: p };
    return p;
  }
  function bustCart() { window.__bbCartCache = null; }

  /* any cart mutation - ours, the theme's or Zapiet's - invalidates the cache */
  if (!window.__bbCartHook) {
    window.__bbCartHook = 1;
    var _bbFetch = window.fetch;
    window.fetch = function (input) {
      try {
        var u = typeof input === 'string' ? input : (input && input.url) || '';
        var isMut = /\/cart\/(add|update|change|clear)/.test(u);
        /* Zapiet's own traffic counts as "busy" too: its calendar call is the
           first thing that happens when a date is picked, and its cart writes
           follow a second or two later. Anything we read in that window lands
           in Shopify's per-session cart queue ahead of Zapiet's write and
           turns a ~400ms write into a ~1.7s one - that is the spinner. */
        if (isMut || /api\.zapiet\.com/.test(u) || /app=zapiet/.test(u)) {
          if (isMut) window.__bbCartCache = null;
          window.__bbCartLastMut = Date.now();
          /* Shopify serialises cart requests per session, so anything we read
             while the app is mid-write lands in the same queue and makes ITS
             request take seconds. Track what is in flight so our own reads
             can stay out of the way. */
          window.__bbCartBusy = (window.__bbCartBusy || 0) + 1;
          var done = function () {
            window.__bbCartBusy = Math.max(0, (window.__bbCartBusy || 1) - 1);
            window.__bbCartLastMut = Date.now();
          };
          return _bbFetch.apply(this, arguments).then(function (r) { done(); return r; },
                                                      function (e) { done(); throw e; });
        }
      } catch (e) {}
      return _bbFetch.apply(this, arguments);
    };
  }

  /* ---------- cart shape helpers ---------- */

  // A "parent" is a cake line the CVP created: _group-data starts type:parent.
  function parents(cart) {
    var seen = {}, out = [];
    (cart.items || []).forEach(function (i) {
      var g = i.properties && i.properties['_group-data'];
      if (typeof g !== 'string' || g.indexOf('type:parent') !== 0) return;
      // One entry per bundle: if the cart transform ever surfaces more than one
      // line from the same group, they are the same cake, not two choices.
      var id = (g.match(/group:([^;]+)/) || [])[1] || i.key;
      if (seen[id]) return;
      seen[id] = 1;
      out.push(i);
    });
    return out;
  }

  // A merged postcard child is invisible in /cart.js, so the cart attributes are
  // the source of truth for "is there a postcard in this cart". Cart attributes
  // survive /cart/clear.js, so the stored group id is checked against the cart:
  // if the cake it belonged to is gone, the marker is stale and gets cleared.
  function savedMessage(cart) {
    var a = cart.attributes || {};
    var v = a[ATTR_KEY], g = a[ATTR_FOR];
    if (typeof v !== 'string' || !v.trim()) return '';
    var live = parents(cart).some(function (p) { return groupIdOf(p) === g; });
    if (!live) { setAttr('', ''); return ''; }
    return v;
  }

  function savedGroup(cart) {
    return (cart.attributes || {})[ATTR_FOR] || '';
  }

  function groupIdOf(item) {
    var g = (item.properties && item.properties['_group-data']) || '';
    var m = g.match(/group:([^;]+)/);
    return m ? m[1] : '';
  }



  // Insert the postcard flag just before "Bundles" so key order matches the
  // old CVP output as closely as possible.

  /* ---------- cart writes ---------- */


  /* 2026-09-28: the postcard is NO LONGER a merge-child of the cake bundle.
     As a child it was folded into the parent line by the cart transform, so
     Zapiet - which stamps _ZapietId line by line against that merged view -
     wrote the parent's properties over the real child lines, wiping _Child and
     _ParentProductID and blowing the bundle apart. It now goes in as its own
     line, keeping only group:<id> so the job sheet can still tie the message
     back to its cake. */
  function addPostcard(parent, message) {
    var props = {
      '_group-data': 'group:' + groupIdOf(parent)
    };
    props[MSG_KEY] = message;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(VARIANT), quantity: 1, properties: props })
    }).then(function (r) { return r.json(); });
  }

  // Mirror of the message, kept where /cart.js can actually see it.
  // X-Postmut-Guard tells the theme's cart.js to skip its post-mutation attribute
  // resync for this request - without it, cart.js immediately overwrites these keys
  // with its own pre-write snapshot.
  function setAttr(value, group) {
    var a = {};
    a[ATTR_KEY] = value;
    a[ATTR_FOR] = group || '';
    lastKnown = { msg: value || '', group: group || '' };
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Postmut-Guard': '1' },
      body: JSON.stringify({ attributes: a })
    }).then(function (r) { return r.json(); });
  }

  // The postcard line is merged into its bundle, so it has no visible line key -
  // remove it by variant id instead.
  function removePostcard() {
    /* The postcard is a hidden child of its bundle, so /cart.js can't see it
       and update.js by variant id does nothing - only change.js reaches it,
       and only one line per call. Loop while the total keeps dropping so a
       cart that somehow has two of them ends up with none. */
    function total() {
      return fetch('/cart.js?t=' + Date.now(), { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (c) { return c.total_price; });
    }
    function once() {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Postmut-Guard': '1' },
        body: JSON.stringify({ id: String(VARIANT), quantity: 0 })
      }).then(function (r) { return r.json(); });
    }
    var tries = 0;
    function step(prev) {
      bustCart();
      return once()
        .then(total)
        .then(function (now) {
          if (now < prev && ++tries < 4) return step(now);
          return now;
        })
        .catch(function () { return prev; });
    }
    return total().then(step).then(function () { bustCart(); });
  }



  /* Swap just the line-item table from a freshly rendered section. A full
     page reload took ~11s on the cart page; this is ~0.3s. Only the table is
     replaced - Zapiet's widget lives in the same section and must not be
     torn down. */
  function refreshItems() {
    return fetch('/cart?sections=main-cart-items', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (j) {
        var html = j && j['main-cart-items'];
        var host = document.querySelector('#main-cart-items table.cart-items');
        if (!html || !host) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var fresh = doc.querySelector('#main-cart-items table.cart-items') || doc.querySelector('table.cart-items');
        if (fresh) host.innerHTML = fresh.innerHTML;
      })
      .catch(function () {});
  }

  /* ---------- refresh the themeʼs own drawer markup ---------- */

  function refreshDrawer() {
    return fetch('/?sections=cart-drawer,cart-icon-bubble')
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var dr = document.querySelector('cart-drawer');
        if (dr && data['cart-drawer']) {
          var tmp = document.createElement('div');
          tmp.innerHTML = data['cart-drawer'];
          var fresh = tmp.querySelector('cart-drawer');
          if (fresh) {
            /* The server renders the drawer closed. Remember which classes were
               holding it open and put them back, or it snaps shut mid-edit and
               leaves the page scroll-locked behind an invisible overlay. */
            var keep = ['active', 'animate', 'is-open', 'open'];
            var had = keep.filter(function (c) { return dr.classList.contains(c); });
            var open = dr.classList.contains('is-empty') ? false : true;
            dr.innerHTML = fresh.innerHTML;
            dr.className = fresh.className;
            had.forEach(function (c) { dr.classList.add(c); });
            if (open) dr.classList.remove('is-empty');
          }
        }
        var bub = document.querySelector('#cart-icon-bubble');
        if (bub && data['cart-icon-bubble']) {
          var t2 = document.createElement('div');
          t2.innerHTML = data['cart-icon-bubble'];
          var fb = t2.querySelector('#cart-icon-bubble');
          if (fb) bub.innerHTML = fb.innerHTML;
        }
      })
      .catch(function () { /* drawer will catch up on next open */ });
  }


  /* ---------- hover preview, matching the product-page swatch preview ---------- */

  var PREVIEW_SIZE = 420, BORDER = '#262967';

  // Shopify CDN sizing suffix: BirthdayCard_240x.png -> BirthdayCard_1000x.png
  function hiresUrl(url) {
    try { return url.replace(/_(\d+)x(?=\.[a-z]+(\?|$))/i, '_1000x'); } catch (e) { return url; }
  }

  function previewEl() {
    var p = document.getElementById('bb-pc-preview');
    if (p) return p;
    p = document.createElement('div');
    p.id = 'bb-pc-preview';
    p.style.cssText = 'position:fixed;z-index:10000;width:' + PREVIEW_SIZE + 'px;height:' + PREVIEW_SIZE +
      'px;pointer-events:none;opacity:0;transition:opacity .12s ease;border-radius:8px;overflow:hidden;' +
      'box-shadow:0 8px 24px rgba(0,0,0,0.25);background:#fff;border:4px solid ' + BORDER + ';box-sizing:border-box;';
    var img = document.createElement('img');
    img.style.cssText = 'display:block;width:100%;height:100%;object-fit:contain;background:#fff;';
    p.appendChild(img);
    document.body.appendChild(p);
    return p;
  }

  function showPreview(imgEl) {
    var p = previewEl(), pi = p.firstChild;
    var url = hiresUrl(imgEl.currentSrc || imgEl.src);
    if (pi.getAttribute('data-target') !== url) {
      pi.setAttribute('data-target', url);
      pi.src = imgEl.src;                       // show what we have, upgrade when ready
      var probe = new Image();
      probe.onload = function () { if (pi.getAttribute('data-target') === url) pi.src = url; };
      probe.src = url;
    }
    var r = imgEl.getBoundingClientRect();
    var left = Math.max(8, Math.min(r.left + r.width / 2 - PREVIEW_SIZE / 2, window.innerWidth - PREVIEW_SIZE - 8));
    var top = r.top - PREVIEW_SIZE - 12;
    if (top < 8) top = r.bottom + 12;
    if (top + PREVIEW_SIZE > window.innerHeight - 8) top = Math.max(8, window.innerHeight - PREVIEW_SIZE - 8);
    p.style.left = left + 'px';
    p.style.top = top + 'px';
    p.style.opacity = '1';
  }

  function hidePreview() {
    var p = document.getElementById('bb-pc-preview');
    if (p) p.style.opacity = '0';
  }

  /* ---------- message box grows with the message ---------- */

  function countChars(ta) {
    var el = document.getElementById('bb-pc-count');
    if (!el || !ta) return;
    var n = ta.value.length;
    el.textContent = n + ' / ' + MAXLEN;
    el.classList.toggle('bb-pc-count--near', n >= MAXLEN - 30);
    el.classList.toggle('bb-pc-count--full', n >= MAXLEN);
  }

  function autoGrow(ta) {
    /* stop growing at the bottom of the postcard image, then scroll */
    var cap = 150;
    var img = document.querySelector('#bb-pc-card .bb-pc-img');
    if (img) {
      var h = img.getBoundingClientRect().height;
      if (h > 0) cap = Math.round(h);
    }
    if (cap < 58) cap = 58;
    ta.style.height = 'auto';
    var want = ta.scrollHeight;
    ta.style.height = Math.min(want, cap) + 'px';
    ta.style.overflowY = want > cap ? 'auto' : 'hidden';
    countChars(ta);
  }

  // Start the message box tall enough that the Add button lines up with the
  // bottom of the postcard image. Measured rather than hard-coded, because the
  // blurb wraps to a different number of lines at different drawer widths.
  // The drawer is often still hidden (zero-size) when the card first renders,
  // so retry briefly until the image actually has a height.
  function scheduleFit() {
    var tries = 0;
    (function attempt() {
      fitToImage();
      var card = document.getElementById('bb-pc-card');
      var img = card && card.querySelector('.bb-pc-img');
      var ok = img && img.getBoundingClientRect().height > 0;
      if (!ok && ++tries < 12) setTimeout(attempt, 250);
    }());
  }

  function fitToImage() {
    var ta = document.getElementById('bb-pc-msg');
    if (ta) autoGrow(ta);
  }

  /* ---------- card ---------- */

  function cardHtml(cart, ps, msg) {
    var img = IMAGE ? '<img class="bb-pc-img" src="' + esc(IMAGE) + '" alt="">' : '';

    // The message box is always open - one less click than a collapsed card.
    // There is no "which cake?" picker: the postcard is one per order, so it just
    // attaches to the first cake in the cart.
    return '' +
      '<div class="bb-pc-row">' + img +
        '<div class="bb-pc-body">' +
          '<div class="bb-pc-title"><span class="bb-pc-titletext">Add a personalised postcard</span><span class="bb-pc-price">' + esc(PRICE).replace(/^\+\s*/, '') + '</span></div>' +
          '<div class="bb-pc-sub">Printed in a handwritten-style font on a Bluebells postcard.</div>' +
          '<div class="bb-pc-tawrap">' +
            '<textarea id="bb-pc-msg" class="bb-pc-ta" rows="2" maxlength="' + MAXLEN + '" placeholder="Write your message\u2026">' + esc(draft) + '</textarea>' +
            '<div class="bb-pc-count" id="bb-pc-count"></div>' +
          '</div>' +
          '<button type="button" class="bb-pc-btn bb-pc-btn--block" data-bb-pc="save">Add</button>' +
        '</div>' +
      '</div>';
  }

  /* Where the card goes: directly under the last product, so it reads as part
     of the order rather than as furniture pinned to the bottom of the panel.
     Works in the drawer and on the full cart page. */
  function onCartPage() {
    return /\/cart(\/|$|\?)/.test(location.pathname + location.search);
  }

  /* The postcard card and the order-notes box read as one panel, so they live
     in a wrapper of their own rather than as loose siblings of the cart form.
     Find-or-create, so a re-render of either one keeps them together. */
  function extrasBox() {
    var box = document.getElementById('bb-extras');
    if (box) return box;
    /* sits after the cart panel, never inside it - the cart form gets replaced
       wholesale by the section-rendering refresh */
    var anchor = document.getElementById('bb-cartbox') ||
                 document.querySelector('cart-items .page-width form, cart-items form, .cart__items');
    var col = document.querySelector('cart-items .page-width');
    box = el('div', 'bb-extras');
    box.id = 'bb-extras';
    if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(box, anchor.nextSibling);
    else if (col) col.appendChild(box);
    else return null;
    return box;
  }

  /* The heading and the line items read as one panel. Wrapping them rather
     than bordering the form keeps the border outside anything the cart's own
     section re-render replaces. */
  function cartBox() {
    if (!onCartPage()) return null;
    var box = document.getElementById('bb-cartbox');
    var form = document.querySelector('cart-items .page-width form.cart__contents, cart-items .page-width form');
    var title = document.querySelector('cart-items .page-width .title-wrapper-with-link');
    if (!box) {
      var col = document.querySelector('cart-items .page-width');
      if (!col || !form) return null;
      box = el('div', 'bb-cartbox');
      box.id = 'bb-cartbox';
      col.insertBefore(box, title || form);
    }
    if (title && title.parentNode !== box) box.appendChild(title);
    if (form && form.parentNode !== box) box.appendChild(form);
    return box;
  }

  function hostSlot() {
    /* Cart page only. Checkout runs through the Zapiet flow on /cart, so every
       customer lands here; the drawer never needed a copy. */
    if (!onCartPage()) return null;
    var box = extrasBox();
    if (box) return { parent: box, before: box.firstChild };
    return null;
  }



  /* ---------- the added postcard as a real cart row ----------
     Nothing we draw beside the table can line up with its columns, so once
     the postcard is on the order we append an actual row to the cart table
     using the theme's own cell classes - image, details, quantity (holding
     the remove control) and total. */

  var TRASH_SVG =
    '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M2 4H3.33333H14" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '<path d="M5.33325 4.00001V2.66668C5.33325 2.31305 5.47373 1.97392 5.72378 1.72387C5.97382 1.47382 6.31296 1.33334 6.66659 1.33334H9.33325C9.68687 1.33334 10.026 1.47382 10.2761 1.72387C10.5261 1.97392 10.6666 2.31305 10.6666 2.66668V4.00001M12.6666 4.00001V13.3333C12.6666 13.687 12.5261 14.0261 12.2761 14.2762C12.026 14.5262 11.6869 14.6667 11.3333 14.6667H4.66659C4.31296 14.6667 3.97382 14.5262 3.72378 14.2762C3.47373 14.0261 3.33325 13.687 3.33325 13.3333V4.00001H12.6666Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>' +
    '</svg>';

  function cartTable() {
    var m = document.getElementById('main-cart-items');
    return m ? m.querySelector('table.cart-items tbody') : null;
  }

  /* When the cake is a build-your-own bundle the postcard becomes a hidden
     child of it, so Shopify folds the $5 into the parent line's total and
     showing our row at $5 as well reads as a double charge. When the cake is
     a plain product the postcard is its own line and the parent is untouched.
     Only adjust in the first case, and only on the line it attached to. */
  function adjustParentTotal(on, parentTitle) {
    var body = cartTable();
    if (!body) return;
    var rows = body.querySelectorAll('tr.cart-item:not(#bb-pc-rowitem)');
    for (var i = 0; i < rows.length; i++) {
      var row = rows[i];
      var name = (row.querySelector('.cart-item__name') || {}).textContent || '';
      var cells = row.querySelectorAll('.cart-item__totals');
      var mine = on && parentTitle && name.trim().indexOf(String(parentTitle).trim().slice(0, 30)) === 0;
      for (var j = 0; j < cells.length; j++) {
        var cell = cells[j];
        var orig = cell.getAttribute('data-bb-orig');
        if (!mine) {
          if (orig) { cell.textContent = orig; cell.removeAttribute('data-bb-orig'); }
          continue;
        }
        if (orig) continue;
        var shown = cell.textContent.replace(/\s+/g, ' ').trim();
        if (!/^\$\d/.test(shown)) continue;
        var cents = Math.round(parseFloat(shown.replace(/[^0-9.]/g, '')) * 100);
        if (!(cents > PRICE_CENTS)) continue;
        cell.setAttribute('data-bb-orig', shown);
        cell.textContent = '$' + ((cents - PRICE_CENTS) / 100).toFixed(2);
      }
    }
  }

  /* The postcard is a real cart line now, so the theme renders its own row for
     it. Ours carries the message and the remove button, so hide the theme's. */
  function hideRealPostcardRow() {
    var body = cartTable();
    if (!body) return;
    var rows = body.querySelectorAll('tr.cart-item:not(#bb-pc-rowitem)');
    for (var i = 0; i < rows.length; i++) {
      var n = (rows[i].querySelector('.cart-item__name') || {}).textContent || '';
      if (n.replace(/\s+/g, ' ').trim().indexOf(TITLE) === 0) rows[i].classList.add('bb-pc-realrow');
    }
  }

  function postcardRow(msg, bundledInto) {
    hideRealPostcardRow();
    var body = cartTable();
    var existing = document.getElementById('bb-pc-rowitem');
    if (!msg || !body) { adjustParentTotal(false); if (existing) existing.remove(); return !!existing; }

    var html =
      '<td class="cart-item__media">' +
        (IMAGE ? '<img class="cart-item__image bb-pc-img" src="' + esc(IMAGE) + '" alt="" loading="lazy">' : '') +
      '</td>' +
      '<td class="cart-item__details">' +
        '<div class="cart-item__name h4 break">Personalised Postcard</div>' +
        '<div class="product-option"><strong>Your message:</strong> &ldquo;' + esc(msg) + '&rdquo;</div>' +
      '</td>' +
      '<td class="cart-item__totals right medium-hide large-up-hide">' + esc(PRICE).replace(/^\+/, '') + '</td>' +
      '<td class="cart-item__quantity">' +
        '<div class="cart-item__quantity-wrapper">' +
          '<button type="button" class="button button--tertiary bb-pc-trash" data-bb-pc="remove" aria-label="Remove personalised postcard">' + TRASH_SVG + '</button>' +
        '</div>' +
      '</td>' +
      '<td class="cart-item__totals right small-hide">' + esc(PRICE).replace(/^\+/, '') + '</td>';

    var tr = existing;
    if (!tr || tr.parentElement !== body) {
      if (tr) tr.remove();
      tr = document.createElement('tr');
      tr.id = 'bb-pc-rowitem';
      tr.className = 'cart-item bb-pc-rowitem';
      body.appendChild(tr);
    }
    if (tr.innerHTML !== html) tr.innerHTML = html;
    adjustParentTotal(true, bundledInto);
    return false;
  }

  /* What the last full render decided to show. A section re-render wipes our
     row and un-hides the theme's, and re-deriving that from /cart.js takes
     about a second - long enough for the customer to watch the lines jump and
     settle. Repaint from this straight away instead, then let the network
     round trip confirm it. */
  var lastPaint = { msg: '', bundledInto: '' };

  function repaintNow() {
    if (!onCartPage()) return;
    postcardRow(lastPaint.msg, lastPaint.bundledInto);
  }

  function mount(cart) {
    var slot = hostSlot();
    if (!slot) return;

    var ps = parents(cart);
    var msg = savedMessage(cart);
    var host = document.getElementById('bb-pc-card');

    if (!ps.length) { if (host) host.remove(); postcardRow(''); return; }

    if (msg && !wantFocus) {
      if (host) host.remove();
      /* visible as its own line = nothing to adjust; missing from /cart.js =
         it was merged into its bundle, so that parent carries the $5 */
      var onItsOwn = (cart.items || []).some(function (i) { return Number(i.variant_id) === Number(VARIANT); });
      var owner = ps.filter(function (p) { return groupIdOf(p) === savedGroup(cart); })[0] || ps[0];
      lastPaint = { msg: msg, bundledInto: onItsOwn ? '' : (owner && owner.title) || '' };
      postcardRow(msg, lastPaint.bundledInto);
      return;
    }
    lastPaint = { msg: '', bundledInto: '' };
    postcardRow('');

    if (!host) {
      host = el('div', 'bb-pc-card');
      host.id = 'bb-pc-card';
      slot.parent.insertBefore(host, slot.before);
    }
    if (!chosenKey || !ps.some(function (p) { return p.key === chosenKey; })) chosenKey = ps[0].key;
    host.innerHTML = cardHtml(cart, ps, msg);
    host.classList.toggle('bb-pc-card--done', !!msg);

    /* the cart sections validate any change event that reaches them and pop a
       native "increments of undefined" bubble over this textarea - keep ours in */
    if (!host.__bbStop) {
      host.__bbStop = 1;
      host.addEventListener('change', function (e) { e.stopPropagation(); }, true);
      host.addEventListener('change', function (e) { e.stopPropagation(); });
    }
    scheduleFit();
    if (wantFocus) {
      wantFocus = false;
      var ta = document.getElementById('bb-pc-msg');
      if (ta) { ta.focus(); ta.setSelectionRange(ta.value.length, ta.value.length); }
    }
  }

  function render() {
    return getCart().then(function (cart) {
      // Did an unguarded cart change wipe our marker? Put it back.
      if (lastKnown.msg && !(cart.attributes || {})[ATTR_KEY] &&
          parents(cart).some(function (p) { return groupIdOf(p) === lastKnown.group; })) {
        return setAttr(lastKnown.msg, lastKnown.group).then(getCart).then(mount);
      }
      return mount(cart);
    }).catch(function () {});
  }

  /* ---------- interactions ---------- */

  document.addEventListener('input', function (e) {
    if (e.target && e.target.id === 'bb-pc-msg') { draft = e.target.value; autoGrow(e.target); }
  });
  document.addEventListener('pointerenter', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('bb-pc-img')) showPreview(e.target);
  }, true);
  document.addEventListener('pointerleave', function (e) {
    if (e.target && e.target.classList && e.target.classList.contains('bb-pc-img')) hidePreview();
  }, true);
  document.addEventListener('scroll', hidePreview, true);
  window.addEventListener('resize', function () { clearTimeout(window.__bbPcResize); window.__bbPcResize = setTimeout(fitToImage, 150); });


  document.addEventListener('click', function (e) {
    var b = e.target && e.target.closest && e.target.closest('[data-bb-pc]');
    if (!b || busy) return;
    var act = b.getAttribute('data-bb-pc');
    e.preventDefault();


    if (act === 'edit') {
      busy = true;
      getCart().then(function (cart) {
        draft = savedMessage(cart);
        return removePostcard().then(function () { return setAttr('', ''); });
      }).then(function () {
        wantFocus = true; busy = false;
        return onCartPage() ? refreshItems().then(render) : refreshDrawer().then(render);
      }).catch(function () { busy = false; render(); });
      return;
    }

    if (act === 'remove') {
      busy = true;
      removePostcard().then(function () { return setAttr('', ''); }).then(function () {
        draft = ''; busy = false;
        return onCartPage() ? refreshItems().then(render) : refreshDrawer().then(render);
      }).catch(function () { busy = false; render(); });
      return;
    }

    if (act === 'save') {
      var ta = document.getElementById('bb-pc-msg');
      var text = (ta ? ta.value : draft).trim();
      if (!text) { if (ta) ta.focus(); return; }
      busy = true;
      b.textContent = 'Saving…';
      // Always re-read the cart: line keys change whenever the cart is touched.
      getCart().then(function (cart) {
        var ps = parents(cart);
        var parent = ps.filter(function (p) { return p.key === chosenKey; })[0] || ps[0];
        if (!parent) throw new Error('no parent');
        // One call: the postcard line carries its own message, so the parent
        // line - and the rest of the merged bundle - is left untouched.
        /* Only clear first if one might already be there. Clearing costs
           several round trips, and on the normal "first postcard" path there
           is nothing to clear - that was most of the wait on Add. */
        var maybeExisting = !!savedMessage(cart) || !!document.getElementById('bb-pc-rowitem');
        var ready = maybeExisting ? removePostcard().then(getCart) : Promise.resolve(cart);
        return ready.then(function (fresh) {
          var fp = parents(fresh).filter(function (p) { return groupIdOf(p) === groupIdOf(parent); })[0] || parents(fresh)[0] || parent;
          return addPostcard(fp, text).then(function () { return setAttr(text, groupIdOf(fp)); });
        });
      }).then(function () {
        draft = ''; busy = false;
        return onCartPage() ? refreshItems().then(render) : refreshDrawer().then(render);
      }).catch(function () {
        busy = false;
        render();
      });
    }
  });

  /* ---------- keep the card alive across drawer re-renders ---------- */

  function watch() {
    var dr = document.querySelector('cart-drawer');
    if (!dr) return;
    var mo = new MutationObserver(function () {
      if (busy) return;
      if (!document.getElementById('bb-pc-card')) render();
    });
    mo.observe(dr, { childList: true, subtree: true });
  }

  /* ---------- two-column cart page ----------
     Items on the left, summary + pickup/delivery on the right. The columns are
     made with CSS on the shared parent, so the Zapiet mount point is never
     moved in the DOM - shifting it re-runs their widget. The only thing that
     travels is the order-note box, which is a plain textarea tied to the cart
     form by its form attribute, so it still submits from the left column. */

  function twoColumn() {
    if (!onCartPage()) return;
    document.documentElement.classList.add('bb-cart2');
    cartBox();

    var note = document.querySelector('cart-note, .cart__note');
    if (!note || note.getAttribute('data-bb-moved') === '1') return;
    var box = extrasBox();
    if (!box) return;
    box.appendChild(note);
    note.setAttribute('data-bb-moved', '1');
    note.classList.add('bb-cart-note');
  }

  function watchItems() {
    var host = document.getElementById('main-cart-items');
    if (!host) return;
    var pending = false;
    /* Repaint ONLY when the section render actually knocked our row out or
       left the theme's postcard row showing. Repainting on every mutation
       feeds this observer its own changes and spins forever. */
    function needsRepaint() {
      if (!lastPaint.msg) return false;
      if (!document.getElementById('bb-pc-rowitem')) return true;
      var rows = host.querySelectorAll('tr.cart-item:not(#bb-pc-rowitem)');
      for (var i = 0; i < rows.length; i++) {
        if (rows[i].classList.contains('bb-pc-realrow')) continue;
        var n = (rows[i].querySelector('.cart-item__name') || {}).textContent || '';
        if (n.replace(/\s+/g, ' ').trim().indexOf(TITLE) === 0) return true;
      }
      return false;
    }
    new MutationObserver(function () {
      if (needsRepaint()) { try { repaintNow(); } catch (e) {} }
      if (pending || busy) return;
      if (document.getElementById('bb-pc-rowitem') || !lastPaint.msg) return;
      pending = true;
      setTimeout(function () { pending = false; render(); }, 300);
    }).observe(host, { childList: true, subtree: true });
  }

  function boot() {
    if (CFG.cssUrl) {
      var link = document.createElement('link');
      link.rel = 'stylesheet'; link.href = CFG.cssUrl;
      document.head.appendChild(link);
    }
    render();
    watchItems();
    setTimeout(twoColumn, 0);
    setTimeout(twoColumn, 600);
    watch();
    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest && e.target.closest('[href$="/cart"], .cart-drawer-toggle, #cart-icon-bubble')) {
        setTimeout(render, 400);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
}());
